import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import SparkLogo from './SparkLogo';
import useAdminUnlock from '../hooks/useAdminUnlock';

/**
 * Navbar — landing site header (fixed).
 *
 * Mobile hamburger is a native <button type="button"> with defensive handling
 * to prevent event propagation, form submission, tap-through and click-ghosting
 * bugs across iOS Safari / Android Chrome. State is fully controlled by React;
 * body scroll is locked while the drawer is open.
 */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef(null);
  const onLogoTap = useAdminUnlock();

  // Scroll listener for glass effect
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  // Body scroll lock while drawer is open (prevents "loading" flash on iOS
  // when the page scrolls behind the fixed header).
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  // Auto-close when user resizes to desktop (>= lg breakpoint)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => { if (e.matches) setMobileOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const openMenu = useCallback((e) => {
    // Defensive: stop any residual propagation / default action
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setMobileOpen((v) => !v);
  }, []);

  const closeMenu = useCallback(() => setMobileOpen(false), []);

  const links = [
    { label: 'Como funciona', href: '#how' },
    { label: 'Quero Investir', href: '#investors' },
    { label: 'Tenho uma ideia', href: '#entrepreneurs' },
    { label: 'Por que aqui', href: '#differentiators' },
    { label: 'Histórias', href: '#testimonials' },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[padding,background,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? 'py-2 sm:py-2.5 bg-[#050816]/70 backdrop-blur-2xl border-b border-white/[0.07] shadow-[0_8px_30px_rgba(2,6,23,0.45)]'
          : 'py-4 sm:py-5 bg-transparent border-b border-transparent'
      }`}
      style={{
        WebkitBackdropFilter: scrolled ? 'blur(28px) saturate(160%)' : 'none',
      }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <a
            href="#top"
            aria-label="GetSpark — Página inicial"
            data-testid="navbar-logo"
            onClick={onLogoTap}
            className="flex items-center shrink-0 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4facfe]/60 rounded-xl transition-transform duration-300 active:scale-[0.98] cursor-pointer select-none"
          >
            <span className="sm:hidden">
              <SparkLogo height={64} />
            </span>
            <span className="hidden sm:block md:hidden">
              <SparkLogo height={76} />
            </span>
            <span className="hidden md:block lg:hidden">
              <SparkLogo height={88} />
            </span>
            <span className="hidden lg:block">
              <SparkLogo height={96} />
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="navbar-desktop-nav hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-[#4facfe] to-[#ffa726] group-hover:w-full transition-all duration-300" />
              </a>
            ))}
            <a
              href="/#planos"
              data-testid="navbar-plans-link"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors relative group"
            >
              Planos
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-[#4facfe] to-[#ffa726] group-hover:w-full transition-all duration-300" />
            </a>
          </nav>

          <div className="navbar-desktop-ctas hidden lg:flex items-center gap-3">
            <a
              href="/auth"
              data-testid="navbar-signin"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-white/[0.04]"
            >
              Entrar
            </a>
            <a
              href="/auth"
              data-testid="navbar-cta"
              className="btn-primary rounded-xl px-5 py-2.5 text-sm font-semibold shadow-[0_8px_28px_rgba(79,172,254,0.35)]"
            >
              Quero começar
            </a>
          </div>

          {/* Mobile hamburger — bulletproof native <button> with custom class */}
          <button
            type="button"
            onClick={openMenu}
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileOpen}
            aria-controls="navbar-mobile-menu"
            data-testid="navbar-mobile-toggle"
            className="navbar-hamburger lg:hidden"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile drawer + backdrop */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              {/* Backdrop — closes menu on tap outside */}
              <motion.div
                key="mobile-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={closeMenu}
                aria-hidden="true"
                className="lg:hidden fixed inset-0 z-[45] bg-black/40 backdrop-blur-sm"
                data-testid="navbar-mobile-backdrop"
              />
              {/* Drawer content */}
              <motion.div
                key="mobile-drawer"
                ref={drawerRef}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                id="navbar-mobile-menu"
                data-testid="navbar-mobile-menu"
                role="dialog"
                aria-modal="true"
                aria-label="Menu principal"
                onClick={(e) => e.stopPropagation()}
                className="lg:hidden relative z-[55] mt-3 rounded-2xl p-5 bg-[#0a0f24]/95 backdrop-blur-2xl border border-white/[0.08] shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
              >
                <div className="flex flex-col gap-1">
                  {links.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      onClick={closeMenu}
                      className="text-slate-200 hover:text-white py-2.5 px-2 text-sm font-medium rounded-lg hover:bg-white/[0.04] transition-colors"
                    >
                      {l.label}
                    </a>
                  ))}
                  <a
                    href="/#planos"
                    onClick={closeMenu}
                    data-testid="navbar-mobile-plans"
                    className="text-slate-200 hover:text-white py-2.5 px-2 text-sm font-medium rounded-lg hover:bg-white/[0.04] transition-colors"
                  >
                    Planos
                  </a>
                  <div className="h-px bg-white/[0.06] my-2" />
                  <a
                    href="/auth"
                    onClick={closeMenu}
                    className="text-slate-200 hover:text-white py-2.5 px-2 text-sm font-medium rounded-lg hover:bg-white/[0.04] transition-colors"
                  >
                    Entrar
                  </a>
                  <a
                    href="/auth"
                    onClick={closeMenu}
                    className="btn-primary rounded-xl px-5 py-3 text-sm font-semibold text-center mt-2"
                  >
                    Quero começar
                  </a>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;
